import { ParametersPanelWidgetState } from '@go-widgets/well-logs-widget';
import type { ParametersInitParams } from '@go-widgets/well-logs-widget';
import { action, makeObservable, observable, reaction } from 'mobx';

import { lagacyAgent } from 'src/api/legacyAgent';
import { wellLogsWidgetAgent } from 'src/api/wellLogsWidgetAgent';
import type { TGroup, TWellListCombinedData } from 'src/entities/tab/TabEntity';
import type { IWidgetStore } from 'src/entities/widget/IWidgetStore';
import { requireService } from 'src/packages/di';

import { setWellIdInGroup } from '../../utils/setWellIdInGroup';
import type { GroupSelectStore } from '../group-select/GroupSelect.store';

import type { OperationalParametersWidgetEntity } from './OperationalParametersWidget.entity';
import { getCorrectLocale } from './OperationalParametersWidget.utils';

export class OperationalParametersWidgetStore implements IWidgetStore {
  private readonly operationalParametersEntity: OperationalParametersWidgetEntity;
  groupSelectStore: GroupSelectStore | null = null;
  wellListPanel: TWellListCombinedData | null = null;
  private readonly initParams: ParametersInitParams;

  @observable isLoading: boolean = false;
  @observable isReady: boolean = false;
  @observable hasError: boolean = false;
  @observable isGroupSelectOpen: boolean = false;

  constructor(
    operationalParametersEntity: OperationalParametersWidgetEntity,
    groupSelectStore?: GroupSelectStore,
    wellListPanel: TWellListCombinedData | null = null,
    private readonly theme = requireService('theme'),
    private readonly stompCachePublisherService = requireService('stompCachePublisherService'),
    private readonly wellStatusService = requireService('wellStatusService'),
    private readonly notifications = requireService('notifications'),
    private readonly language = requireService('language'),
    private readonly classOfUnitsService = requireService('classOfUnitsService'),
    private readonly userPreferencesService = requireService('userPreference'),
  ) {
    const { wellIndexType } = operationalParametersEntity;

    this.operationalParametersEntity = operationalParametersEntity;
    this.isGroupSelectOpen = operationalParametersEntity.isGroupsDefaultOpened;

    if (groupSelectStore) {
      this.groupSelectStore = groupSelectStore;
    }

    this.initParams = {
      index: wellIndexType,
      legacyAxiosInstance: lagacyAgent,
      stompPublisher: this.stompCachePublisherService.stompPublisher,
      wellDirectoryServiceStompPublisher: this.wellStatusService,
      axiosInstance: wellLogsWidgetAgent,
      classOfUnitsService: this.classOfUnitsService,
    };

    this.wellListPanel = wellListPanel;

    makeObservable(this);
  }

  @action.bound
  setIsGroupSelectOpen(isOpen: boolean): void {
    this.isGroupSelectOpen = isOpen;
  }

  @action.bound
  exitFullScreen() {
    this.operationalParametersEntity.setFullscreen(false);
  }

  @action.bound
  setGroupForWellLogs(group: TGroup | null): void {
    this.operationalParametersEntity.setGroup(group ? group.id : null);
  }

  @action.bound
  setWellId(well: number | null): void {
    this.operationalParametersEntity.setWellId(well);
  }

  @action.bound
  setEditOpen() {
    this.operationalParametersEntity.state?.enterEditMode();
  }

  @action.bound
  setGroupSelectStore(groupSelectStore: GroupSelectStore) {
    this.groupSelectStore = groupSelectStore;
  }

  @action.bound
  setWellListPanel(wellListPanel: TWellListCombinedData) {
    this.wellListPanel = wellListPanel;
  }

  @action.bound
  private setReady(value: boolean): void {
    this.isReady = value;
  }

  @action.bound
  private setInActiveTab(value: boolean) {
    this.operationalParametersEntity.setInActiveTab(value);
  }

  private getNewState(wellId: number): ParametersPanelWidgetState {
    return new ParametersPanelWidgetState(
      this.initParams,
      {
        // TODO: remove hardcode
        wellId,
        width: 500,
        height: 500,
        locale: getCorrectLocale(this.language.language),
        theme: this.theme.theme,
        active: this.operationalParametersEntity.inActiveTab,
        isAltNames: this.userPreferencesService.preferences.isAltNames,
        defaultSelectWellbore:
          this.operationalParametersEntity.wellIndexType === 'LOG_TIME'
            ? this.userPreferencesService.preferences.autoSelectWellboreForTime
            : this.userPreferencesService.preferences.autoSelectWellboreForDepth,
      },
      this.operationalParametersEntity.stateParams,
      {
        onInitializedSuccess: () => {
          this.setReady(true);
        },
        onInitializationFailed: (_, error) => {
          this.setReady(false);

          console.error(error);
          this.notifications.showErrorMessageT('errors:failedToInitWidget');
        },
        onInternalStateChanged: (state) => {
          this.operationalParametersEntity.setStateParams(state);
        },
        onAlertRegistered: (state) => {
          this.operationalParametersEntity.setHasAlarm(state);
        },
        onWellboreChanged: (wellboreName) => {
          this.operationalParametersEntity.setWellboreName(wellboreName);
        },
      },
    );
  }

  initWidgetByWellId(wellId: number) {
    const newState = this.getNewState(wellId);
    this.operationalParametersEntity.setState(newState);
  }

  effect = () => {
    this.setInActiveTab(true);

    const handleVisibilityChange = (): void => {
      this.setInActiveTab(document.visibilityState === 'visible');
    };
    window.addEventListener('visibilitychange', handleVisibilityChange);

    const disposeGroupId = reaction(
      () => this.operationalParametersEntity.groupId,
      (groupId) => {
        if (groupId === null || this.groupSelectStore == null) return;

        const widgets = this.groupSelectStore.groupManager.getWidgetsByGroupId(groupId);

        if (this.wellListPanel) {
          setWellIdInGroup(widgets, this.operationalParametersEntity, this.wellListPanel);
        }
      },
    );

    const disposeWellId = reaction(
      () => this.operationalParametersEntity.wellId,
      (wellId, prevWellId) => {
        if (wellId && !this.operationalParametersEntity.state) {
          const newState = this.getNewState(wellId);
          this.operationalParametersEntity.setState(newState);
        } else if (wellId && this.operationalParametersEntity.state && typeof prevWellId !== 'undefined') {
          this.operationalParametersEntity.state.setAppParams({ wellId });
        } else if (!wellId && this.operationalParametersEntity.state) {
          this.operationalParametersEntity.state.destroy();
          this.operationalParametersEntity.setState(null);
        }
      },
      { fireImmediately: true },
    );

    const disposeStateParams = reaction(
      () => ({
        theme: this.theme.theme,
        language: this.language.language,
        isAltNames: this.userPreferencesService.preferences.isAltNames,
      }),
      ({ theme, language, isAltNames }) => {
        this.operationalParametersEntity.state?.setAppParams({
          theme,
          locale: getCorrectLocale(language),
          isAltNames,
        });
      },
      {
        fireImmediately: true,
      },
    );

    const wellListPanelDisposer = reaction(
      () => this.wellListPanel?.store.selectedWellId,
      (wellId) => {
        this.operationalParametersEntity.setWellId(wellId ?? null);
      },
    );

    return () => {
      this.setInActiveTab(false);
      disposeGroupId();
      disposeWellId();
      disposeStateParams();
      wellListPanelDisposer();
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  };
}
